import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationCenter = ({ notifications, onNotificationAction }) => {
  const [filter, setFilter] = useState('all');

  const filterOptions = [
    { value: 'all', label: 'All', icon: 'Bell' },
    { value: 'achievements', label: 'Achievements', icon: 'Award' },
    { value: 'community', label: 'Community', icon: 'Users' },
    { value: 'events', label: 'Events', icon: 'Calendar' }
  ];

  const filteredNotifications = notifications?.filter(notification => {
    if (filter === 'all') return true;
    return notification?.category === filter;
  });

  const handleNotificationClick = (notification) => {
    onNotificationAction?.(notification);
    
    // Navigate based on notification type
    switch (notification?.category) {
      case 'achievements':
        window.location.href = '/progress-tracking?section=achievements';
        break;
      case 'community':
        window.location.href = '/community-forums';
        break;
      case 'events':
        window.location.href = '/events-and-workshops';
        break;
      default:
        break;
    }
  };

  const getNotificationIcon = (category) => {
    switch (category) {
      case 'achievements': return 'Award';
      case 'community': return 'Users';
      case 'events': return 'Calendar';
      case 'system': return 'Settings';
      default: return 'Bell';
    }
  };

  const getNotificationColor = (category) => {
    switch (category) {
      case 'achievements': return 'bg-success';
      case 'community': return 'bg-primary';
      case 'events': return 'bg-accent';
      case 'system': return 'bg-secondary';
      default: return 'bg-muted';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-semibold text-foreground">Notifications</h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">
            {notifications?.filter(n => !n?.read)?.length} unread
          </span>
          <Icon name="Bell" size={20} className="text-primary" />
        </div>
      </div>
      {/* Filter Tabs */}
      <div className="flex items-center space-x-1 mb-4 overflow-x-auto">
        {filterOptions?.map((option) => (
          <button
            key={option?.value}
            onClick={() => setFilter(option?.value)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-wellness whitespace-nowrap ${
              filter === option?.value
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Icon name={option?.icon} size={14} />
            <span>{option?.label}</span>
          </button>
        ))}
      </div>
      {/* Notifications List */}
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {filteredNotifications?.length > 0 ? (
          filteredNotifications?.map((notification, index) => (
            <div
              key={index}
              onClick={() => handleNotificationClick(notification)}
              className={`flex items-start space-x-3 p-3 rounded-lg border transition-wellness cursor-pointer ${
                notification?.read 
                  ? 'border-border hover:border-primary/20 hover:bg-primary/5' :'border-primary/20 bg-primary/5 hover:bg-primary/10'
              }`}
            >
              <div className={`w-8 h-8 ${getNotificationColor(notification?.category)} rounded-full flex items-center justify-center flex-shrink-0`}>
                <Icon name={getNotificationIcon(notification?.category)} size={16} color="white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <p className={`text-sm font-medium ${notification?.read ? 'text-foreground' : 'text-primary'}`}>
                    {notification?.title}
                  </p>
                  {!notification?.read && (
                    <div className="w-2 h-2 bg-primary rounded-full animate-breathing"></div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                  {notification?.message}
                </p>
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={12} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{notification?.time}</span>
                </div>
              </div>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon name="Bell" size={32} className="mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-sm text-muted-foreground">No notifications in this category</p>
          </div>
        )}
      </div>
      {/* Action Buttons */}
      {filteredNotifications?.length > 0 && (
        <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-border">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onNotificationAction?.({ action: 'mark-all-read', filter })}
            className="flex-1"
          >
            Mark All Read
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onNotificationAction?.({ action: 'clear-read', filter })}
            className="flex-1"
          >
            Clear Read
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;